import { useEffect, useState } from 'react';
import { useAPI } from '../contexts/APIContext';
import { useProfile } from '../contexts/ProfileContext';
import logger from '../utils/logger';

const playable = (s) => s && s.file_url && String(s.status || 'GENERATED').toUpperCase() === 'GENERATED';

/**
 * What plays after this story ends.
 *  - A story in the viewer's library (their own or saved): the next one in
 *    the library, in library order (newest first, own before saved).
 *  - Otherwise, or at the end of the library: a recommended Storyverse story.
 * Returns { id, name, thumbnail_url, source: 'library' | 'recommended' } or null.
 */
export function useNextStory(storyId, { signedIn = false, enabled = true } = {}) {
  const { apiClient } = useAPI();
  const { currentProfile } = useProfile();
  const [next, setNext] = useState(null);
  const profileId = currentProfile?.id || null;

  useEffect(() => {
    if (!enabled || !storyId || !apiClient) {
      setNext(null);
      return undefined;
    }
    let cancelled = false;
    setNext(null);
    (async () => {
      try {
        if (signedIn) {
          const lib = await apiClient.getUserStories(profileId);
          const list = Array.isArray(lib?.data) ? lib.data : [];
          const at = list.findIndex((s) => s.id === storyId);
          if (at >= 0) {
            const after = list.slice(at + 1).find(playable);
            if (after) {
              if (!cancelled) setNext({ ...pick(after), source: 'library' });
              return;
            }
          }
        }
        const feed = await apiClient.getStoriverseStories();
        const options = (Array.isArray(feed?.data) ? feed.data : []).filter((s) => s.id !== storyId && playable(s));
        if (options.length && !cancelled) {
          const choice = options[Math.floor(Math.random() * Math.min(options.length, 12))];
          setNext({ ...pick(choice), source: 'recommended' });
        }
      } catch (e) {
        logger.warn('Could not work out the next story:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storyId, signedIn, enabled, apiClient, profileId]);

  return next;
}

const pick = (s) => ({ id: s.id, name: s.name || s.title || 'Next story', thumbnail_url: s.thumbnail_url || s.cover_image_url || null });

export default useNextStory;
